import { Box, Stack } from '@mui/material';
import { getAllSigners } from 'lib/api/business';
import { Signer } from 'lib/api/business/types';
import { preloadBusinessesGlobalSearcher } from 'lib/api/search';
import { PreloadedBusiness } from 'lib/api/search/types';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { errorHandler } from 'lib/functions/errors';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { SingleSelect } from '../../SingleSelect';
import { Aval } from '../types';

type Props = {
  aval: Aval;
  setAval: (aval: Aval) => void;
  value: Aval[];
};

const OtherCompany: React.FC<Props> = ({ aval, setAval, value }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState<PreloadedBusiness | null>(null);
  const [businesses, setBusinesses] = useState<PreloadedBusiness[]>([]);
  const [businessUsers, setBusinessUsers] = useState<Signer[]>([]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const data = await preloadBusinessesGlobalSearcher(dispatch);
      if (data) setBusinesses(data);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      if (!business) return;
      const query = `identifier=${business.object?.identifier}`;
      const response = await getAllSigners(dispatch, query);
      if (response) setBusinessUsers(response.data);
    } catch (error) {
      errorHandler(dispatch, error);
    }
  };

  const getUserLabel = (user: Signer) => {
    if (!user.name) return '';
    const nameParts = user.name.split(' ').filter(Boolean);
    if (nameParts.length === 0) return '';
    if (nameParts.length === 1) return nameParts[0];
    return `${nameParts[0]} ${nameParts[1]}`;
  };

  const handleSelectOtherBusiness = (businessIdentifier: string) => {
    const business = businesses.find(
      (business) => business.value === businessIdentifier,
    );
    if (business) setBusiness(business);
  };

  const setOtherBusinessAval = (identifier: string) => {
    const signer = businessUsers?.find((u) => u.identifier === identifier);
    if (signer) {
      const newAval = {
        ...aval,
        signer: {
          identifier: signer.identifier,
          name: signer.name,
          email: signer.email,
          phone: signer.phone,
          country: signer.country,
          profession: signer.profession,
          maritalStatus: signer.maritalStatus,
        },
        business: {
          name: business?.object?.name ?? '',
          identifier: business?.object?.identifier ?? '',
        },
      };
      setAval(newAval);
    }
  };

  const usersOptions = useMemo(() => {
    const addedAvalIdentifiers = value
      .filter((a) => a.type === 'OTHER_COMPANY' && a.signer?.identifier)
      .map((a) => a.signer!.identifier!);

    return (
      businessUsers
        .map((user) => ({
          value: user.identifier,
          label: getUserLabel(user),
        }))
        .filter((user) => !addedAvalIdentifiers.includes(user.value)) ?? []
    );
  }, [businessUsers, value]);

  useEffect(() => {
    void fetchBusinesses();
  }, []);

  useEffect(() => {
    void fetchUsers();
  }, [business]);

  if (loading)
    return (
      <Box height={100}>
        <CenteredCircularProgress type="dialog" />
      </Box>
    );

  return (
    <Stack direction="column" spacing={2}>
      <SingleSelect
        options={businesses}
        value={business?.value ?? ''}
        onChange={handleSelectOtherBusiness}
        label="Empresa aval"
      />
      <SingleSelect
        options={usersOptions}
        value={aval?.signer?.identifier ?? ''}
        onChange={setOtherBusinessAval}
        label="Aval empresa"
      />
    </Stack>
  );
};

export default OtherCompany;
